import type { CampaignPageQuery } from '@generated/graphql'
import type { PageProps } from 'gatsby'
import { graphql } from 'gatsby'
import CampanhaTemplate from 'src/components/cms/templates/CampanhaTemplate'
import CollectionPLPTemplate from 'src/components/cms/templates/CollectionPLPTemplate'
import { getCampanhaTemplateServerData } from 'src/utils/getCampanhaTemplateServerData'
import type { ServerData } from 'src/utils/getCampanhaTemplateServerData'
import { getCollectionTemplateServerData } from 'src/utils/plp/getCollectionTemplateServerData'
import type { ServerData as CollectionServerData } from 'src/utils/plp/getCollectionTemplateServerData'
import { getCMSPageByContentType } from 'src/utils/getCMSPageDataByContentType'
import { resolveRenderTemplate } from 'src/utils/cms/resolveRenderTemplate'

type CampanhaPageServerData = ServerData['serverData'] & {
  renderTemplate?: undefined
}

type CollectionPLPPageServerData = CollectionServerData['serverData'] & {
  renderTemplate: 'collection-plp'
}

export type Props = PageProps<CampaignPageQuery, any, any, unknown> & {
  serverData: CampanhaPageServerData | CollectionPLPPageServerData
}

const FREE_HTML_SECTION_NAME = '[Campanhas 2.0] HTML livre'

function encodeUriComponentSafely(rawValue: string): string {
  try {
    return encodeURIComponent(rawValue)
  } catch {
    return rawValue
  }
}

function encodeFreeHtmlSections(
  cmsResponse: Awaited<ReturnType<typeof getCMSPageByContentType>>
): void {
  const cmsData = cmsResponse?.props?.cmsData

  if (!cmsData || !Array.isArray(cmsData.sections)) {
    return
  }

  cmsData.sections = cmsData.sections.map((section) => {
    if (
      section?.name !== FREE_HTML_SECTION_NAME ||
      typeof section?.data?.html !== 'string'
    ) {
      return section
    }

    return {
      ...section,
      data: {
        ...section.data,
        html: encodeUriComponentSafely(section.data.html),
      },
    }
  })
}

export default function CampaignPage(props: Props) {
  const { serverData } = props

  if (serverData.renderTemplate === 'collection-plp') {
    return (
      <CollectionPLPTemplate
        serverData={serverData}
        location={props.location}
        data={{ site: props.data.site }}
      />
    )
  }

  return (
    <CampanhaTemplate
      serverData={serverData}
      location={props.location}
      data={{ site: props.data.site }}
    />
  )
}

/**
 * This query is run during SSG
 * */

export const querySSG = graphql`
  query CampaignPage {
    site {
      ...SiteInformation
    }
  }
`

export const getServerData = async ({
  query,
  params: { slug },
}: {
  query: Record<string, string>
  params: Record<string, string>
}) => {
  const nativeCmsData = await getCMSPageByContentType({
    slug,
    contentType: 'campanha',
    pageId: query?.pageId ?? '',
    versionId: query?.versionId ?? '',
  })

  encodeFreeHtmlSections(nativeCmsData)

  const renderTemplate = resolveRenderTemplate(
    nativeCmsData.props?.cmsData ?? null
  )

  if (renderTemplate === 'collection-plp') {
    const result = await getCollectionTemplateServerData({
      slug,
      query,
      routePrefix: 'campanha',
      cmsData: nativeCmsData.props?.cmsData ?? null,
    })

    return { ...result, props: { ...result.props, renderTemplate } }
  }

  return getCampanhaTemplateServerData({
    slug,
    query,
    cmsData: nativeCmsData,
  })
}
